import {Injectable, OnDestroy} from '@angular/core';
import {NGXLogger} from 'ngx-logger';
import {BehaviorSubject, Observable, ReplaySubject, Subject} from 'rxjs';
import {distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {PortalMessageBroker} from './portal-message-broker';


export enum WellKnownShellProperties {
  APP_MODE_NORMAL = 'shell:app-mode:normal',
  APP_MODE_POPUP = 'shell:app-mode:popup',
  APP_MODE_WIDGET = 'shell:app-mode:widget',
  EMBEDDED_EMBEDDED = 'shell:embedded:embedded',
  EMBEDDED_EMBEDDING = 'shell:embedded:embedding',
  SHELL_MODE = 'shell:shell:mode',
}

export enum WellKnownJustFarmingProperties {
  ANALYTICS = 'vendor:de.just-farming:analytics',
  ERROR_REPORTING = 'vendor:de.just-farming:errorReporting',
  ANALYTICS_VID = 'vendor:de.just-farming:analytics.visitorId',
}

export type WellKnownProperties = WellKnownShellProperties | WellKnownJustFarmingProperties;
export type PropertyKey = string | WellKnownProperties;

export type Property = boolean | string | number | undefined;
export type ArrayProperty = Property[];
export type ContextInfoProperty = Property | ArrayProperty;

export const SUPPORTED_MODES = ['default', 'gewerbeportal'] as const;
export type Mode = typeof SUPPORTED_MODES[number];

export interface ContextInfo {
  properties: Map<PropertyKey, ContextInfoProperty>;
}


@Injectable({
  providedIn: 'root'
})
export class ContextInfoService
  implements OnDestroy {

  private readonly unsubscribe$ = new Subject<void>();

  private readonly _contextInfo$ = new BehaviorSubject<ContextInfo>({
    properties: new Map(),
  });

  private readonly _shellMode$ = new ReplaySubject<Mode | undefined>(1);

  private shellModeOverride: Mode | null = null;

  constructor(
    private logger: NGXLogger,
    private portalMessageBroker: PortalMessageBroker,
  ) {
    // Check for shellMode query parameter override
    this.checkQueryParameterOverride();

    // Initialize shellMode$ based on running context
    this.initializeShellMode();

    this.portalMessageBroker.registerContextInfoCallback(contextInfo => {
      this._contextInfo$.next(contextInfo);

      // Update shellMode when context info changes
      if (!this.shellModeOverride) {
        const shellModeValue = contextInfo.properties.get(WellKnownShellProperties.SHELL_MODE);
        this._shellMode$.next(this.mapToMode(shellModeValue));
      }

      this.logger.debug('ContextInfoService: new context info', this._contextInfo$.value);
    });
  }

  private async initializeShellMode(): Promise<void> {
    // If there's a query parameter override, use it immediately
    if (this.shellModeOverride) {
      this._shellMode$.next(this.shellModeOverride);
      return;
    }

    // info: determine if running in portal
    this.portalMessageBroker.runningInPortal$.subscribe(runningInPortal => {
      if (!runningInPortal) {
        // info: not running in portal, emit default immediately
        this._shellMode$.next('default');
        this.logger.debug('ContextInfoService: Not running in portal, using default mode');
      }
    });
  }

  private checkQueryParameterOverride(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const shellModeParam = urlParams.get('shellMode');

    if (shellModeParam && SUPPORTED_MODES.includes(shellModeParam as Mode)) {
      this.shellModeOverride = shellModeParam as Mode;
      this.logger.info(`ContextInfoService: Using shellMode override from query parameter: ${shellModeParam}`);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this._shellMode$.complete();
  }

  get contextInfo(): ContextInfo {
    return this._contextInfo$.value;
  }

  get contextInfo$(): Observable<ContextInfo> {
    return this._contextInfo$.asObservable()
      .pipe(
        takeUntil(this.unsubscribe$),
      );
  }

  get shellMode$(): Observable<Mode> {
    return this._shellMode$.asObservable()
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((mode): mode is Mode => mode !== undefined),
        distinctUntilChanged(),
      );
  }

  private mapToMode(
    value: any,
  ) {
    if (typeof value === 'string' && SUPPORTED_MODES.includes(value as Mode)) {
      return value as Mode;
    }
    this.logger.warn(`ContextInfoService: Unsupported mode value: ${value}, falling back to 'default'`);
    return undefined;
  }

  /**
   * Legt den Wert einer geteilten Eigenschaft fest.
   *
   * Steht anderen Applications über das ContextInfo Objekt zur Verfügung.
   *
   * @param key eindeutiger Identifikator
   * @param value Wert zum angegebenen Schlüssel
   */
  public submitProperty(key: string, value: ContextInfoProperty): void {
    if (this.portalMessageBroker.isRunningInPortal()) {
      this.portalMessageBroker.submitContextInfoProperty(key, value);
    }
  }

}
